import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useMemo } from 'react';

import { Sport } from '#src/__generated__/globalTypes.ts';
import { useQuery } from '#src/hooks/graphql/useQuery.ts';
import { useFeatureFlags } from '#src/hooks/useFeatureFlags.tsx';

import type {
  MatchdayPicksTabQuery,
  MatchdayPicksTabQueryVariables,
} from './__generated__/useMatchdayPicksTab.graphql.ts';

const MATCHDAY_PICKS_TAB_QUERY = gql`
  query MatchdayPicksTabQuery {
    market {
      id
      activeSteppedDynamicCampaigns {
        nodes {
          id
          player {
            slug
          }
        }
      }
    }
  }
` as TypedDocumentNode<MatchdayPicksTabQuery, MatchdayPicksTabQueryVariables>;

// Football-only because `activeSteppedDynamicCampaigns` takes no sport argument and its `player`
// is the football-specific type. `resolving` matters because LaunchDarkly is not deferred, so the
// flag reads `undefined` until the client settles — callers that redirect must not treat that as
// off.
export const useFlashSalesEnabled = ({ sport }: { sport: Sport }) => {
  const { flags } = useFeatureFlags();
  const flag = flags.enableFlashSalesTab;

  return {
    resolving: flag === undefined,
    enabled: !!flag && sport === Sport.FOOTBALL,
  };
};

export const isLiveMatchdayPick = (campaign: {
  player?: { slug: string } | null;
}) => !!campaign.player;

// Gated on the campaign alone, never on `primaryOffer`: that field is null for a campaign that has
// nothing on sale and has never sold, which is a live sale with no buyable pick yet — the entry
// point should still be there.
export const useMatchdayPicksTab = ({ sport }: { sport: Sport }) => {
  const { enabled } = useFlashSalesEnabled({ sport });

  const { data } = useQuery(MATCHDAY_PICKS_TAB_QUERY, {
    fetchPolicy: 'cache-first',
    skip: !enabled,
  });

  const campaigns = useMemo(
    () =>
      (data?.market?.activeSteppedDynamicCampaigns.nodes || []).filter(
        isLiveMatchdayPick
      ),
    [data?.market?.activeSteppedDynamicCampaigns.nodes]
  );

  return {
    show: enabled && campaigns.length > 0,
  };
};
